/**
 * @fileoverview Shared {{mergeField}} placeholder helpers for recreated/edited document layouts.
 * @module document-render/placeholderTokens
 *
 * Responsibilities: - Collect the distinct {{token}} merge-field placeholders used across a layout.
 *                   - Rename placeholders across every band element (confirm-and-bind UX, G-AI-UX-01).
 * Dependencies:     - ./documentLayout (DocumentLayout, Band).
 * Architecture:     - Pure functions — no I/O, no side effects. Consumed by the builder pages
 *                     (placeholder review modal) so tenant-web and system-web share one rule.
 * Notes:            - Tokens are lowerCamelCase identifiers ([A-Za-z0-9_]); unmatched tokens are
 *                     left unchanged. The {{advField:...}} tokens are a separate namespace and are
 *                     never matched here (they contain a ':').
 */

import type { DocumentLayout, Band } from './documentLayout';

const TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;

/** Walk header + body + footer + cover-letter bands of a layout. */
function allBands(layout: DocumentLayout): Band[] {
  return [
    ...(layout.header ? [layout.header] : []),
    ...layout.body,
    ...(layout.footer ? [layout.footer] : []),
    ...(layout.coverLetter?.bands ?? []),
  ];
}

/**
 * Collect the distinct {{token}} merge-field placeholders across all band element HTML, sorted.
 *
 * @param layout DocumentLayout to scan.
 * @returns Distinct token names (without the braces), sorted alphabetically.
 */
export function collectPlaceholders(layout: DocumentLayout): string[] {
  const found = new Set<string>();
  for (const band of allBands(layout)) {
    for (const el of band.elements) {
      if (typeof el.html !== 'string') continue;
      let m: RegExpExecArray | null;
      TOKEN_RE.lastIndex = 0;
      while ((m = TOKEN_RE.exec(el.html)) !== null) found.add(m[1]);
    }
  }
  return Array.from(found).sort();
}

/**
 * Rename {{token}} placeholders across every band element's HTML in a layout.
 * Only entries whose target differs from the source (and is a valid token) are applied;
 * the original layout is not mutated.
 *
 * @param layout DocumentLayout whose element HTML may contain tokens.
 * @param rename Map of oldTokenName → newTokenName (names without braces).
 * @returns A new DocumentLayout with tokens renamed.
 */
export function renamePlaceholdersInLayout(
  layout: DocumentLayout,
  rename: Record<string, string>,
): DocumentLayout {
  const effective = Object.entries(rename).filter(
    ([from, to]) => from !== to && /^[a-zA-Z0-9_]+$/.test(from) && /^[a-zA-Z0-9_]+$/.test(to),
  );
  if (effective.length === 0) return layout;
  const map = new Map(effective);

  const renameInHtml = (html: string): string =>
    html.replace(TOKEN_RE, (match, name: string) => (map.has(name) ? `{{${map.get(name)}}}` : match));

  const renameBand = (band: Band): Band => ({
    ...band,
    elements: band.elements.map((el) => (typeof el.html === 'string' ? { ...el, html: renameInHtml(el.html) } : el)),
  });

  return {
    ...layout,
    header: layout.header ? renameBand(layout.header) : layout.header,
    footer: layout.footer ? renameBand(layout.footer) : layout.footer,
    body: layout.body.map(renameBand),
    coverLetter: layout.coverLetter
      ? { ...layout.coverLetter, bands: layout.coverLetter.bands.map(renameBand) }
      : layout.coverLetter,
  };
}
